import type { KanhaChatConfig } from "./types";
import { resolveWorkerModuleUrl } from "./browser-retrieval";
import { onSpeechProgress, speechWorkerRequest, type SynthesizedSpeech } from "./speech";

declare const __KANHA_SPEECH_WORKER_URL__: string;

export const DEFAULT_READ_ALOUD_MODEL = "Kanha-AI/supertonic-tts-2-fp16-ONNX";
export const DEFAULT_READ_ALOUD_MODEL_REVISION = "5848bad597d9faf407998f5850d848fa0301744f";
export const DEFAULT_READ_ALOUD_VOICE = "F1";
export const DEFAULT_READ_ALOUD_LANGUAGE = "en";
export const READ_ALOUD_LANGUAGES = ["en", "ko", "es", "pt", "fr"] as const;
export const MAX_SPEECH_CHUNK_CHARS = 240;
export const SPEECH_CHUNK_GAP_SECONDS = 0.18;
export const READ_ALOUD_FAILED_MESSAGE = "Could not read this answer aloud. Please try again.";

export type ReadAloudLanguage = (typeof READ_ALOUD_LANGUAGES)[number];
export type ReadAloudState = "idle" | "preparing" | "speaking";

export interface ReadAloudStatus {
  messageIndex: number | null;
  state: ReadAloudState;
  loadProgress: number | null;
}

type ReadAloudConfig = Pick<
  KanhaChatConfig,
  "readAloudModel" | "readAloudVoice" | "readAloudLanguage" | "speechWorkerUrl"
>;

const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>()\]]+/gi;

export function readAloudSupported() {
  return typeof Worker !== "undefined" && typeof AudioContext !== "undefined";
}

export function readAloudIdentity(config: ReadAloudConfig) {
  const model = config.readAloudModel || DEFAULT_READ_ALOUD_MODEL;
  return {
    model,
    revision: model === DEFAULT_READ_ALOUD_MODEL ? DEFAULT_READ_ALOUD_MODEL_REVISION : "main",
  };
}

export function readAloudVoiceUrl(model: string, revision: string, voice?: string) {
  const name = voice || DEFAULT_READ_ALOUD_VOICE;
  if (/^https?:\/\//i.test(name)) return name;
  return `https://huggingface.co/${model}/resolve/${revision}/voices/${encodeURIComponent(name)}.bin`;
}

function spokenUrl(url: string) {
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/[?#].*$/, "")
    .replace(/[/.,;:!]+$/, "")
    .split("/")
    .filter(Boolean)
    .map((part) => part.replace(/\.(?=\w)/g, " dot "))
    .join(" slash ");
}

export function speakableText(markdown: string) {
  const lines = markdown
    .replace(/```[\s\S]*?(?:```|$)/g, "\n")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(URL_PATTERN, spokenUrl)
    .replace(/`([^`]*)`/g, "$1")
    .replace(/^[ \t]{0,3}#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]{0,3}>[ \t]?/gm, "")
    .replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm, "")
    .replace(/^[ \t]*\|?(?:[ \t]*:?-{3,}:?[ \t]*\|?)+[ \t]*$/gm, "")
    .replace(/[ \t]*\|[ \t]*/g, ", ")
    .replace(/(\*\*|~~|\*)(?=\S)([^*~]*?\S)\1/g, "$2")
    .replace(/(?<!\w)_(?=\S)([^_]*?\S)_(?!\w)/g, "$1")
    .replace(/(?<=\w)_(?=\w)/g, " ")
    .replace(/\p{Extended_Pictographic}️?/gu, "")
    .replace(/[<>*]/g, "")
    .split("\n");
  return lines
    .map((line) => line.replace(/\s+/g, " ").replace(/^[,\s]+|[,\s]+$/g, ""))
    .filter((line) => /[\p{L}\p{N}]/u.test(line))
    .map((line) => (/[.!?:;]$/.test(line) ? line : `${line}.`))
    .join(" ");
}

function splitLong(sentence: string, maxChars: number) {
  if (sentence.length <= maxChars) return [sentence];
  const pieces: string[] = [];
  let current = "";
  for (let word of sentence.split(/(?<=[,;:])\s+|\s+/)) {
    if (word.length > maxChars) {
      if (current) {
        pieces.push(current);
        current = "";
      }
      while (word.length > maxChars) {
        pieces.push(word.slice(0, maxChars));
        word = word.slice(maxChars);
      }
    }
    if (current && current.length + word.length + 1 > maxChars) {
      pieces.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) pieces.push(current);
  return pieces;
}

export function splitSpeechChunks(text: string, maxChars = MAX_SPEECH_CHUNK_CHARS) {
  const sentences = text
    .split(/(?<=[.!?])\s+(?=\S)/)
    .map((sentence) => sentence.trim())
    .filter(Boolean)
    .flatMap((sentence) => splitLong(sentence, maxChars));
  const chunks: string[] = [];
  for (const sentence of sentences) {
    const last = chunks[chunks.length - 1];
    if (chunks.length > 1 && last.length + sentence.length + 1 <= maxChars) {
      chunks[chunks.length - 1] = `${last} ${sentence}`;
    } else {
      chunks.push(sentence);
    }
  }
  return chunks;
}

export interface ReadAloud {
  toggle(messageIndex: number, content: string): void;
  stop(): void;
  dispose(): void;
}

export function createReadAloud(options: {
  config: () => ReadAloudConfig;
  onChange: (status: ReadAloudStatus) => void;
  onError: (message: string) => void;
}): ReadAloud {
  let status: ReadAloudStatus = { messageIndex: null, state: "idle", loadProgress: null };
  let session = 0;
  let context: AudioContext | null = null;
  let sources: AudioBufferSourceNode[] = [];
  let activeModel = "";

  const update = (next: Partial<ReadAloudStatus>) => {
    status = { ...status, ...next };
    options.onChange(status);
  };
  const idle = () => update({ messageIndex: null, state: "idle", loadProgress: null });
  const unsubscribe = onSpeechProgress((model, progress) => {
    if (status.state !== "preparing" || model !== activeModel) return;
    const loadProgress = progress < 1 ? Math.round(progress * 100) : null;
    if (loadProgress !== status.loadProgress) update({ loadProgress });
  });

  const stopPlayback = () => {
    for (const source of sources) {
      source.onended = null;
      try {
        source.stop();
      } catch {
      }
    }
    sources = [];
  };

  const stop = () => {
    session += 1;
    stopPlayback();
    if (status.state !== "idle") idle();
  };

  const speak = async (messageIndex: number, content: string) => {
    const current = ++session;
    stopPlayback();
    const chunks = splitSpeechChunks(speakableText(content));
    if (chunks.length === 0) {
      if (status.state !== "idle") idle();
      return;
    }
    try {
      const config = options.config();
      const { model, revision } = readAloudIdentity(config);
      const moduleUrl = resolveWorkerModuleUrl(
        config.speechWorkerUrl,
        __KANHA_SPEECH_WORKER_URL__,
        "speechWorkerUrl",
      );
      const voice = readAloudVoiceUrl(model, revision, config.readAloudVoice);
      const language = config.readAloudLanguage || DEFAULT_READ_ALOUD_LANGUAGE;
      activeModel = model;
      update({ messageIndex, state: "preparing", loadProgress: null });
      context ??= new AudioContext();
      const audio = context;
      const resumed = audio.resume();
      await resumed;
      if (current !== session) return;

      const synthesize = (text: string) => {
        const request = speechWorkerRequest<SynthesizedSpeech>(
          { type: "synthesize", model, revision, text, voice, language },
          moduleUrl,
        );
        request.catch(() => {});
        return request;
      };
      let next = synthesize(chunks[0]);
      let startAt = 0;
      for (let i = 0; i < chunks.length; i += 1) {
        const speech = await next;
        if (current !== session) return;
        if (i + 1 < chunks.length) next = synthesize(chunks[i + 1]);
        const buffer = audio.createBuffer(1, speech.audio.length, speech.sampleRate);
        buffer.copyToChannel(speech.audio, 0);
        const source = audio.createBufferSource();
        source.buffer = buffer;
        source.connect(audio.destination);
        startAt = Math.max(startAt, audio.currentTime + 0.05);
        source.start(startAt);
        startAt += buffer.duration + SPEECH_CHUNK_GAP_SECONDS;
        sources.push(source);
        if (i === 0) update({ state: "speaking", loadProgress: null });
        if (i === chunks.length - 1) {
          source.onended = () => {
            if (current !== session) return;
            sources = [];
            idle();
          };
        }
      }
    } catch {
      if (current !== session) return;
      stopPlayback();
      idle();
      options.onError(READ_ALOUD_FAILED_MESSAGE);
    }
  };

  return {
    toggle(messageIndex, content) {
      if (status.messageIndex === messageIndex && status.state !== "idle") stop();
      else void speak(messageIndex, content);
    },
    stop,
    dispose() {
      stop();
      unsubscribe();
      void context?.close();
      context = null;
    },
  };
}
